import React, { useRef } from "react";
import Link from "../audience/ScientificLink";
import "./AgentInfrastructureCompact.css";
import { motion, useInView, useReducedMotion } from "framer-motion";
import {
  ArrowDown,
  ArrowRight,
  Bot,
  Cable,
  Database,
  FlaskConical,
  Pipette,
  Route,
  ScanLine,
  ServerCog,
  ShieldCheck,
  Thermometer,
  Workflow,
} from "lucide-react";

const agentNodes = [
  {
    icon: FlaskConical,
    label: "Assay planner",
    action: "Builds the run plan",
  },
  {
    icon: Bot,
    label: "Method agent",
    action: "Prepares instrument steps",
  },
  {
    icon: Workflow,
    label: "Recovery agent",
    action: "Handles known failure paths",
  },
];

const hardwareNodes = [
  {
    icon: Pipette,
    label: "Liquid handler",
    action: "Runs approved liquid steps",
  },
  {
    icon: ScanLine,
    label: "Plate reader",
    action: "Returns measurements + status",
  },
  {
    icon: Thermometer,
    label: "Incubator",
    action: "Reports state + alarms",
  },
];

const traceEvents = [
  {
    kind: "Plan",
    route: "Scientist → Assay planner",
    detail: "Approved inputs and operating limits attached",
  },
  {
    kind: "Handoff",
    route: "Runtime coordinator → Method agent",
    detail: "Instrument method requested",
  },
  {
    kind: "Device call",
    route: "Method agent → Runtime coordinator → Instrument gateway",
    detail: "Command checked against the supported device schema",
  },
  {
    kind: "Device event",
    route: "Liquid handler → Instrument gateway → Runtime coordinator",
    detail: "Run state and instrument response recorded",
  },
  {
    kind: "Approval",
    route: "Runtime coordinator → Scientist",
    detail: "Retry or resume held for review",
  },
];

const proofPoints = [
  {
    icon: Cable,
    title: "Connect supported instruments in your workcell",
    body: "Create typed, permissioned tools around supported vendor APIs, drivers, schedulers, and instrument software.",
  },
  {
    icon: ShieldCheck,
    title: "Gate every agent-issued hardware action",
    body: "Validate inputs, restrict actions, require approval where needed, and retain vendor interlocks and E-stops.",
  },
  {
    icon: Route,
    title: "Trace software and hardware together",
    body: "Correlate agent handoffs with commands, device responses, errors, retries, approvals, and artifacts.",
  },
];

const SystemRail = ({ label, nodes, hardware = false }) => (
  <div className="agent-system-rail overflow-hidden rounded-2xl border border-slate-300 bg-slate-50/80 dark:border-white/[0.12] dark:bg-white/[0.025]">
    <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-white/[0.09] sm:px-5">
      <p className="font-mono text-[9px] font-medium uppercase tracking-[0.16em] text-slate-500 dark:text-white/[0.48]">
        {label}
      </p>
      <span className="flex items-center gap-1.5 font-mono text-[8px] uppercase tracking-[0.12em] text-primary-600 dark:text-primary-400">
        <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
        {hardware ? "connected" : "coordinated"}
      </span>
    </div>
    <div className="divide-y divide-slate-200 dark:divide-white/[0.09]">
      {nodes.map(({ icon: Icon, label: nodeLabel, action }, index) => (
        <div key={nodeLabel} className="agent-system-node flex min-h-[5.25rem] items-center gap-3 px-4 py-4 sm:px-5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/[0.06] text-primary-600 dark:border-primary-400/25 dark:bg-primary/[0.1] dark:text-primary-400">
            <Icon className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold tracking-[-0.01em] text-slate-900 dark:text-white">
              {nodeLabel}
            </p>
            <p className="mt-1 text-[11px] leading-5 text-slate-500 dark:text-white/[0.48]">
              {action}
            </p>
          </div>
          <span className="ml-auto self-start pt-0.5 font-mono text-[8px] text-slate-400 dark:text-white/25">
            0{index + 1}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const FlowFan = ({ direction, label, inView, reducedMotion }) => {
  const inbound = direction === "in";
  const paths = inbound
    ? [
        "M4 42 C48 42 62 130 116 130",
        "M4 130 C50 130 68 130 116 130",
        "M4 218 C48 218 62 130 116 130",
      ]
    : [
        "M4 130 C58 130 72 42 116 42",
        "M4 130 C52 130 70 130 116 130",
        "M4 130 C58 130 72 218 116 218",
      ];

  const dots = inbound
    ? [
        { x: [4, 48, 82, 116], y: [42, 48, 108, 130] },
        { x: [4, 50, 82, 116], y: [130, 130, 130, 130] },
        { x: [4, 48, 82, 116], y: [218, 212, 152, 130] },
      ]
    : [
        { x: [4, 56, 84, 116], y: [130, 118, 58, 42] },
        { x: [4, 52, 84, 116], y: [130, 130, 130, 130] },
        { x: [4, 56, 84, 116], y: [130, 142, 202, 218] },
      ];

  return (
    <>
      <div className="hidden min-h-[18rem] items-center justify-center lg:flex" aria-hidden="true">
        <div className="relative h-[16.25rem] w-full">
          <svg viewBox="0 0 120 260" className="absolute inset-0 h-full w-full" preserveAspectRatio="none">
            {paths.map((path, index) => (
              <g key={path}>
                <path
                  d={path}
                  fill="none"
                  strokeWidth="6"
                  className="stroke-primary/[0.045] dark:stroke-primary-400/[0.045]"
                />
                <motion.path
                  d={path}
                  fill="none"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  className="stroke-primary-600/45 dark:stroke-primary-400/40"
                  initial={reducedMotion ? false : { pathLength: 0, opacity: 0.2 }}
                  animate={inView ? { pathLength: 1, opacity: 1 } : {}}
                  transition={{
                    duration: 0.82,
                    delay: reducedMotion ? 0 : 0.24 + index * 0.1,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                />
              </g>
            ))}
            {!reducedMotion &&
              dots.map(({ x, y }, index) => (
                <motion.circle
                  key={`${direction}-signal-${index}`}
                  r="3.6"
                  className="fill-primary-600 dark:fill-primary-400"
                  initial={{ cx: x[0], cy: y[0], opacity: 0 }}
                  animate={inView ? { cx: x, cy: y, opacity: [0, 1, 1, 0.18] } : {}}
                  transition={{
                    duration: 1.05,
                    delay: 0.7 + index * 0.13 + (inbound ? 0 : 0.24),
                    ease: [0.16, 1, 0.3, 1],
                  }}
                />
              ))}
          </svg>
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap bg-white/90 px-2 py-1 font-mono text-[7px] font-medium uppercase tracking-[0.13em] text-primary-700 dark:bg-[#0a0d13]/90 dark:text-primary-400">
            {label}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 border-y border-slate-200 py-2 font-mono text-[8px] font-medium uppercase tracking-[0.13em] text-primary-700 dark:border-white/[0.08] dark:text-primary-400 lg:hidden">
        <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
        {label}
      </div>
    </>
  );
};

const RuntimeControl = () => (
  <div className="agent-runtime-control overflow-hidden rounded-2xl border border-primary/25 bg-white shadow-[0_18px_48px_rgba(15,23,42,0.1)] dark:border-primary-400/25 dark:bg-[#0d121a] dark:shadow-[0_22px_54px_rgba(0,0,0,0.28)]">
    <div className="border-b border-primary/15 bg-primary/[0.045] px-4 py-3 dark:border-primary-400/15 dark:bg-primary/[0.075]">
      <p className="font-mono text-[9px] font-medium uppercase tracking-[0.16em] text-primary-700 dark:text-primary-400">
        Control plane
      </p>
    </div>
    <div className="agent-runtime-node px-4 py-5 text-center sm:px-5">
      <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-white shadow-[0_10px_28px_rgba(5,117,230,0.24)]">
        <Workflow className="h-5 w-5" aria-hidden="true" />
      </span>
      <p className="mt-3 text-sm font-semibold text-slate-950 dark:text-white">Runtime coordinator</p>
      <p className="mt-1 text-[10px] leading-5 text-slate-500 dark:text-white/[0.48]">
        Routes state, approvals, and recovery
      </p>
    </div>
    <div className="agent-runtime-node relative border-t border-slate-200 px-4 py-5 text-center dark:border-white/[0.09] sm:px-5">
      <span className="absolute left-1/2 top-0 h-3 w-px -translate-x-1/2 -translate-y-full bg-primary/50" aria-hidden="true" />
      <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl border border-primary/20 bg-primary/[0.06] text-primary-700 dark:border-primary-400/25 dark:bg-primary/[0.1] dark:text-primary-400">
        <Cable className="h-5 w-5" aria-hidden="true" />
      </span>
      <p className="mt-3 text-sm font-semibold text-slate-950 dark:text-white">Instrument gateway</p>
      <p className="mt-1 text-[10px] leading-5 text-slate-500 dark:text-white/[0.48]">
        Validates typed hardware actions
      </p>
      <p className="mt-3 font-mono text-[8px] uppercase tracking-[0.12em] text-slate-400 dark:text-white/30">
        API · driver · scheduler
      </p>
    </div>
  </div>
);

const RunTrace = ({ inView, reducedMotion }) => (
  <div className="grid border-t border-slate-200 bg-slate-50/70 dark:border-white/[0.1] dark:bg-white/[0.02] lg:grid-cols-[0.28fr_0.72fr]">
    <div className="border-b border-slate-200 p-5 dark:border-white/[0.1] sm:p-7 lg:border-b-0 lg:border-r">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary-600 dark:text-primary-400">
            Correlated run log
          </p>
          <h3 className="mt-2 max-w-xs text-xl font-semibold tracking-[-0.02em] text-slate-950 dark:text-white">
            One record across agents and instruments
          </h3>
        </div>
        <Route className="h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
      </div>

      <div className="mt-7 border-t border-primary/20 bg-primary/[0.045] p-4 dark:border-primary-400/20 dark:bg-primary/[0.07]">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-white text-primary-700 dark:border-primary-400/25 dark:bg-[#0d121a] dark:text-primary-400">
            <Database className="h-4 w-4" aria-hidden="true" />
          </span>
          <div>
            <p className="font-mono text-[9px] font-medium uppercase tracking-[0.14em] text-primary-700 dark:text-primary-400">
              Run trace
            </p>
            <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-white/[0.58]">
              Agent messages, device commands, responses, approvals,
              errors, retries, and artifacts stay linked to one run.
            </p>
          </div>
        </div>
      </div>
    </div>

    <ol className="divide-y divide-slate-200 dark:divide-white/[0.09]">
      {traceEvents.map((event, index) => (
        <motion.li
          key={`${event.kind}-${event.route}`}
          initial={reducedMotion ? false : { opacity: 0, x: 10 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{
            duration: 0.42,
            delay: reducedMotion ? 0 : 0.48 + index * 0.1,
            ease: [0.16, 1, 0.3, 1],
          }}
          className="grid gap-2 px-5 py-4 sm:grid-cols-[2rem_minmax(9rem,0.55fr)_minmax(0,1fr)] sm:gap-3 sm:px-7"
        >
          <span className="pt-0.5 font-mono text-[9px] tracking-[0.12em] text-slate-400 dark:text-white/30">
            {String(index + 1).padStart(2, "0")}
          </span>
          <div>
            <p className="font-mono text-[9px] font-medium uppercase tracking-[0.14em] text-primary-700 dark:text-primary-400">
              {event.kind}
            </p>
            <p className="mt-1 text-xs font-semibold text-slate-800 dark:text-white/[0.78]">
              {event.route}
            </p>
          </div>
          <p className="text-xs leading-5 text-slate-500 dark:text-white/[0.48] sm:border-l sm:border-slate-200 sm:pl-4 dark:sm:border-white/[0.09]">
            {event.detail}
          </p>
        </motion.li>
      ))}
    </ol>
  </div>
);

const WorkflowDiagram = ({ inView, reducedMotion }) => (
  <div className="agent-workflow-diagram grid items-center gap-4 lg:grid-cols-[minmax(0,1fr)_7rem_minmax(12rem,0.74fr)_7rem_minmax(0,1fr)] lg:gap-0">
    <SystemRail label="Software agents" nodes={agentNodes} />
    <FlowFan direction="in" label="plan + handoff" inView={inView} reducedMotion={reducedMotion} />
    <RuntimeControl />
    <FlowFan direction="out" label="commands + status" inView={inView} reducedMotion={reducedMotion} />
    <SystemRail label="Physical lab hardware" nodes={hardwareNodes} hardware />
  </div>
);

const AgentInfrastructureSection = ({ compact = false }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.08 });
  const reducedMotion = useReducedMotion();

  const reveal = (delay = 0) =>
    reducedMotion
      ? {}
      : {
          initial: { opacity: 0, y: 18 },
          animate: inView ? { opacity: 1, y: 0 } : {},
          transition: { duration: 0.58, delay, ease: [0.16, 1, 0.3, 1] },
        };

  if (compact) {
    return (
      <section
        id="agent-infrastructure-story"
        ref={ref}
        data-primary-nav="solutions"
        className="agent-infrastructure-compact section-anchor"
        aria-labelledby="agent-trace-visual-title"
      >
        <figure className="agent-compact-figure">
          <figcaption className="agent-compact-caption">
            <ServerCog aria-hidden="true" />
            <span id="agent-trace-visual-title">Illustrative agent-to-instrument workflow</span>
          </figcaption>
          <div className="agent-compact-diagram">
            <WorkflowDiagram inView={inView} reducedMotion={reducedMotion} />
          </div>
          <p className="agent-compact-boundary">
            Scientists review plans and retries. Hardware acts through supported interfaces,
            with vendor safety controls and interlocks retained.
          </p>
          <details className="agent-compact-trace">
            <summary>See a run trace</summary>
            <RunTrace inView={inView} reducedMotion={reducedMotion} />
            <p className="agent-compact-boundary">
              Illustrative trace. Available controls and telemetry depend on the instrument and vendor interface.
            </p>
          </details>
        </figure>
        <div className="agent-compact-links">
          <Link
            to="/solutions?service=agent-runtime-infrastructure#agent-infrastructure-story"
            data-cta="agent-runtime-infrastructure"
            data-solution-id="agent-runtime-infrastructure"
          >
            Discuss an instrument pilot <ArrowRight aria-hidden="true" />
          </Link>
          <Link
            to="/solutions?service=agent-tracing-observability#agent-infrastructure-story"
            data-cta="agent-tracing-observability"
            data-solution-id="agent-tracing-observability"
          >
            Trace one instrument run <ArrowRight aria-hidden="true" />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section
      id="agent-infrastructure-story"
      ref={ref}
      data-primary-nav="solutions"
      className="section-anchor relative overflow-hidden border-b border-slate-200 bg-[#f3f7fb] px-6 py-24 text-slate-950 dark:border-white/[0.08] dark:bg-[#07090d] dark:text-white sm:py-32"
      aria-labelledby="agent-infrastructure-title"
    >
      <div className="agent-trace-backdrop pointer-events-none absolute inset-0" aria-hidden="true" />

      <div className="wide-shell-xl relative mx-auto max-w-7xl">
        <div className="grid gap-10 lg:grid-cols-[0.37fr_0.63fr] lg:gap-20">
          <motion.p
            {...reveal()}
            className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400"
          >
            Agent-to-instrument infrastructure
          </motion.p>

          <motion.div {...reveal(0.05)}>
            <h2
              id="agent-infrastructure-title"
              className="max-w-5xl font-serif text-5xl font-normal leading-[0.98] tracking-[-0.035em] [text-wrap:pretty] sm:text-6xl lg:text-7xl"
            >
              We build agentic infrastructure that talks to your lab hardware.
            </h2>
            <p className="mt-7 max-w-3xl text-lg leading-8 text-slate-600 dark:text-white/70">
              We connect coordinated agents to the liquid handlers, plate
              readers, incubators, microscopes, and other instruments in scope
              through their supported software interfaces. Every agent handoff,
              device command, instrument response, approval, error, retry, and
              artifact can be tied to the same run.
            </p>
          </motion.div>
        </div>

        <motion.figure
          {...reveal(0.12)}
          aria-labelledby="agent-trace-visual-title"
          className="mt-16 overflow-hidden rounded-[1.75rem] border border-slate-300 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.1)] dark:border-white/[0.12] dark:bg-[#0a0d13] dark:shadow-none"
        >
          <figcaption className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 dark:border-white/[0.1] sm:flex-row sm:items-center sm:justify-between sm:px-7">
            <span
              id="agent-trace-visual-title"
              className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-600 dark:text-white/60"
            >
              <ServerCog className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
              Illustrative agent-to-instrument workflow
            </span>
            <span className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.14em] text-primary-700 dark:text-primary-400">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
              Run trace connected
            </span>
          </figcaption>

          <div className="p-5 sm:p-8 lg:p-10">
            <div className="flex items-start justify-between gap-6">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary-600 dark:text-primary-400">
                  Agent + instrument workflow
                </p>
                <h3 className="mt-2 max-w-2xl text-xl font-semibold tracking-[-0.02em] text-slate-950 dark:text-white sm:text-2xl">
                  Agents coordinate the work. Hardware acts through controlled interfaces.
                </h3>
              </div>
              <Workflow className="h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            </div>

            <div className="mt-8">
              <WorkflowDiagram inView={inView} reducedMotion={reducedMotion} />
            </div>
          </div>

          <RunTrace inView={inView} reducedMotion={reducedMotion} />

          <p className="border-t border-slate-200 px-5 py-4 text-xs leading-6 text-slate-500 dark:border-white/[0.1] dark:text-white/[0.45] sm:px-7">
            Illustrative workflow. Available controls and telemetry depend on
            the instrument and vendor interface; the trace covers only what is
            connected and instrumented.
          </p>
        </motion.figure>

        <motion.div
          {...reveal(0.2)}
          className="mt-10 grid border-l border-t border-slate-300 dark:border-white/[0.12] lg:grid-cols-3"
        >
          {proofPoints.map(({ icon: Icon, title, body }) => (
            <article
              key={title}
              className="border-b border-r border-slate-300 p-6 dark:border-white/[0.12] sm:p-7"
            >
              <Icon className="h-5 w-5 text-primary-600 dark:text-primary-400" aria-hidden="true" />
              <h3 className="mt-5 text-lg font-semibold tracking-[-0.015em] text-slate-950 dark:text-white">
                {title}
              </h3>
              <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-white/[0.58]">{body}</p>
            </article>
          ))}
        </motion.div>

        <motion.div
          {...reveal(0.24)}
          className="mt-10 flex flex-col gap-7 border-t border-slate-300 pt-8 dark:border-white/[0.14] lg:flex-row lg:items-center lg:justify-between"
        >
          <p className="max-w-3xl text-sm leading-7 text-slate-600 dark:text-white/[0.65]">
            We scope the exact devices, interfaces, and allowed actions before
            implementation. Agents do not bypass vendor safety controls,
            instrument interlocks, E-stops, or accountable human review. Where
            hardware exposes limited control or telemetry, we design around
            that constraint instead of implying full autonomy.
          </p>
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
            <Link
              to="/solutions?service=agent-runtime-infrastructure#agent-infrastructure-story"
              data-cta="agent-runtime-infrastructure"
              data-solution-id="agent-runtime-infrastructure"
              className="group inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-primary-600 px-5 py-3 text-sm font-semibold text-white outline-none transition-colors hover:bg-primary-700 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#f3f7fb] dark:focus-visible:ring-offset-[#07090d]"
            >
              Design an agent-to-hardware pilot
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
            </Link>
            <Link
              to="/solutions?service=agent-tracing-observability#agent-infrastructure-story"
              data-cta="agent-tracing-observability"
              data-solution-id="agent-tracing-observability"
              className="inline-flex min-h-12 items-center justify-center rounded-xl border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-800 outline-none transition-colors hover:border-primary hover:text-primary-700 focus-visible:ring-2 focus-visible:ring-primary dark:border-white/[0.16] dark:text-white dark:hover:border-primary-400 dark:hover:text-primary-400"
            >
              Trace one instrument run
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AgentInfrastructureSection;
